import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Link, NewLink } from '../../classroom360/links/link.model';
import { Card } from '../../student360/student-summary/card-container/card/card';
import { SharedService } from '../shared.service';
import { TranslationService } from '../translation/translation.service';
import { Year } from '../year/year.model';
import { System } from './../System';
import { GoogleUser } from './google-user.model';
import { UserPermission } from './user-permission.model';
import { UserRole } from './user-role.model';
import { UserSchoolYearPolicies } from './user-school-year-policies.model';
import { UserSchoolYear } from './user-school-year.model';
import { User } from './user.model';

@Injectable()
export class UserService {
    public userId = 0;
    public username = '';
    public password = '';
    public firstname = '';
    public lastname = '';
    private _cards: Card[];
    public userType = 0;
    public studentId = 0;
    public links = [];
    private _currentUser: User;
    public observableCurrentUser = new Subject<User>();
    observableCurrentUserStream$ = this.observableCurrentUser.asObservable();
    public isLoggedIn = new Subject<boolean>();
    forceUserToClassic: boolean;
    isLoggedInStream$ = this.isLoggedIn.asObservable();
    staffUserStudentLocked: boolean;
    studentInformationView: boolean;
    userRoles: UserRole[];
    googleUser: GoogleUser;

    public observableStudentInfoRoute = new Subject<string>();
    observableStudentInfoRouteStream$ = this.observableStudentInfoRoute.asObservable();

    public selectedStudentInfoPortal: string;

    get languageId() {
        let languageId: number;
        switch (this.currentLanguage) {
            case 'fr':
                languageId = 2;
                break;
            case 'es':
                languageId = 3;
                break;
            default:
                languageId = 1;
        }
        return languageId;
    }

    public get currentUser(): User {
        return this._currentUser;
    }

    public set currentUser(value: User) {
        this._currentUser = value;
        this.observableCurrentUser.next(value);
    }

    public get currentLanguage(): string {
        return this.translateService.currentLanguage;
    }

    public set currentLanguage(value: string) {
        this.translateService.currentLanguage = value;
    }

    public get cards() {
        return this._cards;
    }

    public set cards(cards: Card[]) {
        this._cards = cards;
    }

    constructor(
        private sharedService: SharedService,
        private http: HttpClient,
        private translateService: TranslationService
    ) {
        this.isLoggedIn.next(false);
    }

    logoutUser() {
        this.isLoggedIn.next(false);
        this.userId = 0;
        this.username = '';
        this.password = '';
        this.firstname = '';
        this.lastname = '';
        this.userType = 0;
        this.studentId = 0;
        this.sharedService.updateIsPageLoaded(false);
    }

    getUserLanguage() {
        // POST: gets the current user language
        // NOTE: this will set the current language and translation by default
        const queryString = '?studentId=' + this.studentId;
        const query = this.http.get<string>(System.apiURL.Student + 'GetUserLanguage' + queryString).subscribe(result => {
            this.currentLanguage = result;
        });
    }

    getUserSchoolYearPolicies(yearId: number, schoolId: number): Observable<UserSchoolYearPolicies> {
        let paramString = '';
        paramString += '?yearId=' + yearId;
        paramString += '&schoolId=' + schoolId;
        return this.http.get<UserSchoolYearPolicies>(System.apiURL.Student + 'GetUserSchoolYearPolicies' + paramString);
    }

    getAllUserSchoolYearPolicies(yearId: number): Observable<UserSchoolYearPolicies[]> {
        const paramString = '?yearId=' + yearId;
        return this.http.get<UserSchoolYearPolicies[]>(System.apiURL.Student + 'GetAllUserSchoolYearPolicies' + paramString);
    }

    getUserPermissions(yearId: number, schoolId: number): Observable<UserPermission[]> {
        let paramString = '';
        paramString += '?yearId=' + yearId;
        paramString += '&schoolId=' + schoolId;
        return this.http.get<UserPermission[]>(System.apiURL.Student + 'GetUserPermission' + paramString);
    }

    getUserRoles(yearId: number): Observable<UserRole[]> {
        return this.http.get<UserRole[]>(System.apiURL.Student + 'GetUserRoles?yearId=' + yearId);
    }

    /**
     * Changes the users email
     * @param newEmail the new email for the user
     * @return null if email is invalid. Otherwise, returns an observable for the API call.
     */
    setUserEmail(newEmail: string): Observable<HttpResponse<string>> {
        const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,4})+$/;
        if (!newEmail.match(mailFormat)) {
            return null;
        } else {
            return this.http.get<string>(
                System.apiURL.Student +
                'SaveUserEmail?' +
                'userId=' + this.currentUser.Id +
                '&email=' + newEmail,
                { observe: 'response' }
            );
        }
    }

    emailAlreadyExists(email: string): Observable<boolean> {
        const queryString = '?email=' + email;
        return this.http.get<boolean>(System.apiURL.Student + 'EmailAlreadyExists' + queryString);
    }

    canUserChangeEmail(email: string): Observable<boolean> {
        const queryString = '?email=' + email;
        return this.http.get<boolean>(System.apiURL.Student + 'CanUserChangeEmail' + queryString);
    }

    setUserLanguage(language: string): Observable<any> {
        // PRE: language must be 'en' for English, 'fr' for French, or 'es' for Spanish, or 'pt' for portuguese
        // POST: sets the user's language to that language
        this.currentLanguage = language;
        const queryString = '?studentId=' + this.studentId +
            '&language=' + language;
        return this.http.get<any>(System.apiURL.Student + 'SaveUserLanguage' + queryString);
    }

    changeUserPassword(userType: number, isExpired: boolean, area: string, data: string[]): Observable<any> {
        return this.http.post(System.apiURL.Student + `SaveUserPassword?userType=${userType}&isExpired=${isExpired}&area=${area}`, data, { observe: 'response' });
    }

    getDefaultYear(userId: number): Observable<Year> {
        let paramString = '';
        paramString += '?userId=' + userId;
        return this.http.get<Year>(System.apiURL.Student + 'GetDefaultYear' + paramString);

    }

    getLinks(userId: number, studentId: number, ayIdentifier: string): Observable<any> {
        return this.http.get(System.apiURL.Student + `getMyLinks?userid=${userId}&studentID=${studentId}&ayidentifier=${ayIdentifier}`, { observe: 'response' });
    }

    getParentStudentLinks(studentId: number, yearId: number): Observable<any> {
        return this.http.get(System.apiURL.Student + `GetParentStudentLinks?studentId=${studentId}&yearId=${yearId}`, { observe: 'response' });
    }

    saveLink(link: NewLink): Observable<any> {
        return this.http.post(System.apiURL.Student + 'SaveLink', link, { observe: 'response' });
    }

    deleteLink(link: Link): Observable<any> {
        return this.http.post(System.apiURL.Student + `DeleteLink?linkId=${link.Link_ID}`, link, { observe: 'response' });
    }

    updateLinkSequence(links: NewLink[]): Observable<any> {
        return this.http.post(System.apiURL.Student + 'UpdateLinkSequence', links, { observe: 'response' });
    }

    userLinks() {
        return this.links;
    }

    getImpersonateByUser(): Observable<number> {
        return this.http.get<number>(System.apiURL.Student + 'GetImpersonateByUser');
    }

    setNotificationPreferencesMenu(): Observable<boolean> {
        return this.http.get<boolean>(System.apiURL.Student + 'SetNotificationPreferencesMenu');
    }

    markWelcomeAsSeen(studentId: number): Observable<any> {
        return this.http.get(System.apiURL.Student + `MarkWelcomeAsSeen?studentId=${studentId}`, { observe: 'response' });
    }

    updateStudentViewRoute(route: string) {
        this.observableStudentInfoRoute.next(route);
    }

    getUserPermissionByPrivilegeIds(yearId: number, siteId: number, privilegeIds: number[]): Observable<UserPermission[]> {
        let paramString = '';
        paramString += '?yearId=' + yearId;
        paramString += '&siteId=' + siteId;
        return this.http.post<UserPermission[]>(System.apiURL.Student + 'GetUserPermissionByPrivilegeIds' + paramString, privilegeIds);
    }

    getUserPermissionBySchoolIds(YearId: number, privilegeId: number, siteIds: number[]): Observable<UserPermission[]> {
        let paramString = '';
        paramString += '?YearId=' + YearId;
        paramString += '&privilegeId=' + privilegeId;
        return this.http.post<UserPermission[]>(System.apiURL.Student + 'GetUserPermissionBySchoolIds' + paramString, siteIds);
    }

    isGALDSRoleAssigned(siteId: number): Observable<boolean> {
        // PRE: the site to query at
        // POST: returns true if the user has the GALDS permission for this site
        const paramString = '?schoolId=' + siteId;
        return this.http.get<boolean>(System.apiURL.Student + 'IsGALDSRolAssigned' + paramString);
    }

    isAZEdsRoleAssigned(): Observable<boolean> {
        // POST: returns true if the user has the AZEds permission
        return this.http.get<boolean>(System.apiURL.Student + 'IsAZEdsRolAssigned');
    }

    processADEConnect(): Observable<string> {
        // POST: attempts to connect to AZEds
        return this.http.get<string>(System.apiURL.Student + 'ProcessADEConnect');
    }

    getUserIsTeacher(schoolId: number, yearId: number): Observable<boolean> {
        let paramString = '';
        paramString += '?schoolId=' + schoolId +
            '&yearId=' + yearId;

        return this.http.get<boolean>(System.apiURL.Student + 'GetUserIsTeacher' + paramString);
    }

    getUserIsNurse(schoolId: number, yearId: number): Observable<boolean> {
        let paramString = '';
        paramString += '?schoolId=' + schoolId +
            '&yearId=' + yearId;
        return this.http.get<boolean>(System.apiURL.Student + 'GetUserIsNurse' + paramString);
    }

    getSchoolYearByFacultyStaffId(yearId: number, facultyStaffId: number): Observable<UserSchoolYear[]> {
        let paramString = '';
        paramString += '?yearId=' + yearId +
            '&facultyStaffId=' + facultyStaffId;
        return this.http.get<UserSchoolYear[]>(System.apiURL.Student + 'GetSchoolYearByFacultyStaffId' + paramString);
    }

}